import { useState, useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { useTheme } from 'styled-components';

import Image from '@klv/atoms/image';
import { hasImage } from '@klv/shared/utils/functions';
import { createButtonsArray, setButtonColor } from './utils/functions';
import {
  BodyCopy, HoverImageContainer, RelatedContentLink, StyledButtons, StyledEyebrow, StyledHeadline, StyledRelatedContentCard,
} from './styles';
import type { IRelatedContentCard } from './types';

const RelatedContentCard = ({
  bodyCopy, buttons, eyebrow, headline, hoverImage, link, solidCardHoverColor = 'none', variant, ...rest
}: IRelatedContentCard) => {
  const cardButtons = buttons ? createButtonsArray({ buttons, url: link }) : [];
  const hasHoverImage = hasImage(hoverImage);
  const [isHovered, setIsHovered] = useState(false);
  const openInNewTab = buttons?.length && buttons[0].openInNewTab;
  const theme = useTheme();
  const { t } = useTranslation();

  const [currentButtonColor, setCurrentButtonColor] = useState(setButtonColor({
    hasHoverImage, isHovered, variant, solidCardHoverColor, theme,
  }));

  useEffect(() => {
    setCurrentButtonColor(setButtonColor({
      hasHoverImage, isHovered, variant, solidCardHoverColor, theme,
    }));
  }, [hasHoverImage, isHovered, solidCardHoverColor, variant, theme]);

  return (
    <StyledRelatedContentCard
      $hasImage={hasHoverImage}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      $solidCardHoverColor={solidCardHoverColor}
      $variant={variant}
      {...rest}
    >
      <StyledEyebrow content={eyebrow} />
      <RelatedContentLink
        aria-label={openInNewTab ? `${headline} (${t('molecules_relatedCard_openInNewTab')}
        )` : ''}
        href={link}
        onBlur={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        target={openInNewTab ? '_blank' : '_self'}
      >
        <StyledHeadline forwardedAs="h3" variant="editorial">{headline}</StyledHeadline>
      </RelatedContentLink>
      {bodyCopy && (<BodyCopy dangerouslySetInnerHTML={{ __html: bodyCopy }} variant="paragraph2" />)}
      <StyledButtons aria-hidden buttonColor={currentButtonColor} setTabIndex={-1} buttons={cardButtons} variants={['tertiary']} />
      {hoverImage?.gatsbyImage && (
        <HoverImageContainer>
          <Image altText={hoverImage?.altText || ''} imageData={hoverImage.gatsbyImage} />
        </HoverImageContainer>
      )}
    </StyledRelatedContentCard>
  );
};

export default RelatedContentCard;
